import { useAuth } from "@/src/contexts/auth.context";
import {
	useCreateSeason,
	useCreateUserStarBalance,
	useGetCurrentActiveSeason,
	useSyncStarTransactionPolicy,
	useUpdateSeason,
	useUpdateSeasonConfig,
} from "@/src/services/api/rewardly/rewardly.hooks";
import { useGetActiveUsers } from "@/src/services/users.blocks.api";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";

export type SeasonSchedule = Readonly<{
  startDate: string | null;
  startTime: string;
  endDate: string | null;
  endTime: string;
}>;

export const useConfigure = () => {
  const { t } = useTranslation();
  const { orgId } = useAuth();

  const { mutateAsync: createSeason, isPending: isCreating } = useCreateSeason(
    orgId ?? undefined,
  );
  const { mutateAsync: updateSeason, isPending: isUpdating } =
    useUpdateSeason();
  const { mutateAsync: updateSeasonConfig, isPending: isSavingConfig } =
    useUpdateSeasonConfig();
  const { mutateAsync: createUserStarBalance, isPending: isCreatingBalances } =
    useCreateUserStarBalance();
  const { mutateAsync: syncTransactionPolicy } =
    useSyncStarTransactionPolicy();
  const { data, isLoading, refetch } = useGetCurrentActiveSeason(orgId ?? "");
  const { data: usersData } = useGetActiveUsers();

  const activeSeason = data?.getseasons?.items?.[0];
  const activeUsers = usersData?.data?.data || [];

  // Form state (Configure tab sections)
  const [seasonName, setSeasonName] = useState("");
  const [monetaryValue, setMonetaryValue] = useState("");
  const [minQty, setMinQty] = useState("");
  const [maxQty, setMaxQty] = useState("");
  const [memberStars, setMemberStars] = useState("");
  const [schedule, setSchedule] = useState<SeasonSchedule>({
    startDate: null,
    startTime: "",
    endDate: null,
    endTime: "",
  });

  const activeSeasonId = activeSeason?.ItemId;

  /** Syncs the editable form back to the active season's persisted values. */
  const resetForm = useCallback(() => {
    if (!activeSeason) return;
    setSeasonName(activeSeason.name ?? "");
    setMinQty(
      activeSeason.minGiveAmt != null ? String(activeSeason.minGiveAmt) : "",
    );
    setMaxQty(
      activeSeason.maxGiveAmt != null ? String(activeSeason.maxGiveAmt) : "",
    );
    setMemberStars(
      activeSeason.seasonAllowance != null
        ? String(activeSeason.seasonAllowance)
        : "",
    );
    setSchedule({
      startDate: activeSeason.startDate
        ? activeSeason.startDate.split("T")[0]
        : null,
      startTime: activeSeason.startDate ?? "",
      endDate: activeSeason.endDate ? activeSeason.endDate.split("T")[0] : null,
      endTime: activeSeason.endDate ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSeasonId]);

  useEffect(() => {
    if (!activeSeasonId) return;
    resetForm();
  }, [activeSeasonId, resetForm]);

  const allocateStarsToUsers = async (
    seasonId: string,
    seasonAllowance: number,
  ) => {
    const starBalancePromises = activeUsers.map((activeUser: any) => {
      return createUserStarBalance({
        userId: activeUser.itemId,
        seasonId: seasonId,
        seasonAllowance: seasonAllowance,
        userInfo: {
          email: activeUser.email || "",
          profileImageUrl: activeUser.profileImageUrl || "",
          userId: activeUser.itemId,
          displayName: `${activeUser.firstName} ${activeUser.lastName}`,
        },
      });
    });

    const results = await Promise.allSettled(starBalancePromises);
    const failures = results.filter((result) => result.status === "rejected");

    if (failures.length > 0) {
      Toast.show({
        type: "error",
        text1: t("ERROR_USERS_ALLOCATION_FAILED", { count: failures.length }),
      });
    }

    return results;
  };

  const runPolicyAction = async (action: () => Promise<void>) => {
    try {
      await action();
    } catch (error) {
      console.error("Error syncing star transaction amount policy:", error);
      Toast.show({ type: "error", text1: t("WARN_AMOUNT_POLICY_FAILED") });
    }
  };

  const handleCreateSeason = async () => {
    try {
      const seasonAllowance = Number(memberStars);
      const variables = {
        name: seasonName.trim(),
        startDate: schedule.startTime || new Date(),
        ...(schedule.endTime ? { endDate: schedule.endTime } : {}),
        maxGiveAmt: Number(maxQty),
        minGiveAmt: Number(minQty),
        status: "active",
        seasonAllowance,
      };

      const result = await createSeason(variables);
      const seasonId = result?.insertseason?.itemId;

      if (seasonId && activeUsers.length > 0) {
        await allocateStarsToUsers(seasonId, seasonAllowance);
      }

      // Once the season exists, update the shared schema-access policy that
      // guards starTransaction amount with this season's min/max bounds.
      if (seasonId) {
        await runPolicyAction(() =>
          syncTransactionPolicy({
            minGiveAmt: Number(minQty),
            maxGiveAmt: Number(maxQty),
          }),
        );
      }

      Toast.show({ type: "success", text1: t("MSG_SEASON_CREATED") });
      await refetch();
      return result;
    } catch (error) {
      console.error("Error creating season:", error);
      // The backend rejects duplicate season names with a VALIDATION_ERROR;
      // surface that specifically instead of the generic failure message.
      const gqlMessage =
        (error as any)?.response?.errors?.[0]?.message ?? "";
      const isDuplicateName = /same value for 'name'/i.test(gqlMessage);
      Toast.show({
        type: "error",
        text1: isDuplicateName
          ? t("ERROR_SEASON_NAME_EXISTS")
          : t("ERROR_SEASON_CREATE_FAILED"),
      });
      // Don't re-throw: the error is handled via the toast. Re-throwing turns
      // the button's async onPress into an unhandled rejection (red crash box).
    }
  };

  /** "Save" while a season is active: pushes the current config. Returns
   * whether the save succeeded so the edit modal can close on success only. */
  const handleSaveConfig = async (): Promise<boolean> => {
    if (!activeSeasonId) return false;
    try {
      await updateSeasonConfig({
        filter: JSON.stringify({ ItemId: activeSeasonId }),
        maxGiveAmt: Number(maxQty),
        minGiveAmt: Number(minQty),
        seasonAllowance: Number(memberStars),
        ...(schedule.endTime ? { endDate: schedule.endTime } : {}),
      });
      // Keep the schema-access policy in sync with the edited bounds.
      await runPolicyAction(() =>
        syncTransactionPolicy({
          minGiveAmt: Number(minQty),
          maxGiveAmt: Number(maxQty),
        }),
      );
      Toast.show({ type: "success", text1: t("MSG_SETTINGS_SAVED") });
      await refetch();
      return true;
    } catch (error) {
      console.error("Error saving season config:", error);
      Toast.show({ type: "error", text1: t("ERROR_SETTINGS_SAVE_FAILED") });
      return false;
    }
  };

  const handleEndSeason = async (seasonId: string) => {
    try {
      const variables = {
        filter: JSON.stringify({ ItemId: seasonId }),
        status: "closed",
        endDate: new Date(),
      };

      await updateSeason(variables);
      // The season is over — reset the shared amount policy bounds to 0 so it
      // no longer constrains starTransaction creates.
      await runPolicyAction(() =>
        syncTransactionPolicy({ minGiveAmt: 0, maxGiveAmt: 0 }),
      );
      Toast.show({ type: "success", text1: t("MSG_SEASON_ENDED") });
      await refetch();
    } catch (error) {
      console.error("Error ending season:", error);
      Toast.show({ type: "error", text1: t("ERROR_SEASON_END_FAILED") });
    }
  };

  const isFormValid =
    seasonName.trim().length > 0 &&
    Number(minQty) > 0 &&
    Number(maxQty) >= Number(minQty) &&
    Number(memberStars) > 0;

  return {
    // Data
    activeSeason,
    isLoading: isLoading || isCreatingBalances,
    isCreating,
    isUpdating,
    isSavingConfig,

    // Form state
    seasonName,
    setSeasonName,
    monetaryValue,
    setMonetaryValue,
    minQty,
    setMinQty,
    maxQty,
    setMaxQty,
    memberStars,
    setMemberStars,
    schedule,
    setSchedule,
    isFormValid,

    // Handlers
    handleCreateSeason,
    handleSaveConfig,
    handleEndSeason,
    resetForm,
  };
};

export type ConfigureState = ReturnType<typeof useConfigure>;
